import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Search, X } from "lucide-react";
import { motion } from "framer-motion";
import {
  Backdrop,
  UserSearchModal,
  SearchHeader,
  RoleSelector,
  RoleTab,
  SearchInputWrapper,
  UserList,
  UserItem,
  Avatar,
} from "../styles";
import useConfig from "@hooks/useConfig";
import { Helper } from "@utils/index";

interface Props {
  onClose: () => void;
  onSelect: (participantId: number, role: string) => void;
  directory: any[];
  fetchDirectory: (role: string) => Promise<void>;
}

const NewConversationModal: React.FC<Props> = ({
  onClose,
  onSelect,
  directory,
  fetchDirectory,
}) => {
  const [role, setRole] = useState("store");
  const [searchTerm, setSearchTerm] = useState("");
  const { bucketUrl } = useConfig();

  useEffect(() => {
    fetchDirectory(role);
  }, [role, fetchDirectory]);

  const filteredDirectory = directory.filter((item) => {
    const name = item.storeName || item.fullName || item.name || item.username || "";
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return createPortal(
    <Backdrop
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <UserSearchModal
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        <SearchHeader>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h3 style={{ margin: 0, fontSize: "22px", fontWeight: 800, letterSpacing: "-0.5px" }}>New Conversation</h3>
            <div 
              style={{ 
                width: "32px", 
                height: "32px", 
                borderRadius: "50%", 
                background: "#f1f5f9", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center", 
                cursor: "pointer",
                transition: "all 0.2s"
              }}
              onClick={onClose}
              onMouseEnter={(e) => e.currentTarget.style.background = "#e2e8f0"}
              onMouseLeave={(e) => e.currentTarget.style.background = "#f1f5f9"}
            >
              <X size={18} color="#64748b" />
            </div>
          </div>
          <RoleSelector>
            {["store", "customer", "delivery"].map((r) => (
              <RoleTab
                key={r}
                active={role === r}
                onClick={() => setRole(r)}
              >
                {r.charAt(0).toUpperCase() + r.slice(1)}
              </RoleTab>
            ))}
          </RoleSelector>
          <SearchInputWrapper>
            <Search size={18} />
            <input
              placeholder={`Search ${role === 'delivery' ? 'delivery men' : role + 's'}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </SearchInputWrapper>
        </SearchHeader>

        <UserList>
          {filteredDirectory.length === 0 ? (
            <div style={{ padding: "60px 20px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
              <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Search size={22} color="#cbd5e1" />
              </div>
              <div style={{ color: "#94a3b8", fontSize: "14px", fontWeight: 500 }}>
                No {role === 'delivery' ? 'delivery men' : role + 's'} found matching your search.
              </div>
            </div>
          ) : (
            filteredDirectory.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.03 }}
              >
                <UserItem
                  onClick={() => {
                    onSelect(item.id, role);
                    onClose();
                  }}
                >
                  <Avatar
                    src={
                      (item.logo || item.image || item.avatar)
                        ? `${bucketUrl}/${item.logo || item.image || item.avatar}`
                        : null
                    }
                    style={{
                      backgroundColor: !(item.logo || item.image || item.avatar)
                        ? Helper.getDynamicColor(item.storeName || item.fullName || item.name || item.email || "User")
                        : undefined,
                    }}
                  >
                    {!(item.logo || item.image || item.avatar) &&
                      Helper.getInitials(item.storeName || item.fullName || item.name || item.email || "User")}
                  </Avatar>
                  <div className="info">
                    <div className="name">{item.storeName || item.fullName || item.name || item.username}</div>
                    <div className="role" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: role === 'store' ? '#3b82f6' : role === 'customer' ? '#10b981' : '#f59e0b' }} />
                      {role === 'delivery' ? 'Delivery Man' : role}
                    </div>
                  </div>
                </UserItem>
              </motion.div>
            ))
          )}
        </UserList>
      </UserSearchModal>
    </Backdrop>,
    document.body
  );
};

export default NewConversationModal;
